import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faClock,
  faMapMarkerAlt,
  faArrowLeft,
  faFilter,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../dasboard-component/Components/HeaderMain";
import ConfirmationModal from "../../common/ConfirmationModal";
import "./SportRequests.css";
import { useAuth } from "../../context/AuthContext";

const SportRequests = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const requestsPerPage = 10;

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user?.uid) fetchRequests();
  }, [user?.uid]);

  useEffect(() => {
    filterRequests();
  }, [requests, statusFilter, searchTerm]);

  const fetchRequests = async () => {
    try {
      const userId = user?.uid;
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/user-invitations?userId=${userId}&limit=100`,
        { withCredentials: true }
      );
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = requests;

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((request) => {
        if (statusFilter === "missed") {
          return (
            isDatePassed(request.date, request.time) ||
            request.status === "missed"
          );
        }
        return request.status === statusFilter;
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (request) =>
          request.inviter_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          request.arena_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredRequests(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";

    // Handle both time formats
    if (timeString.includes("T")) {
      const date = new Date(timeString);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } else {
      // Handle HH:MM:SS format
      const [hours, minutes] = timeString.split(":");
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes), 0);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
  };

  const isDatePassed = (date, time) => {
    try {
      // Create proper date object from the booking date and time
      let bookingDate;

      if (date.includes("T")) {
        // If date already contains time
        bookingDate = new Date(date);
      } else {
        // Combine date and time
        if (time && time.includes("T")) {
          const timeOnly = new Date(time).toTimeString().split(" ")[0];
          bookingDate = new Date(`${date}T${timeOnly}`);
        } else {
          bookingDate = new Date(`${date}T${time || "00:00:00"}`);
        }
      }

      const now = new Date();
      return bookingDate < now;
    } catch (error) {
      console.error("Error parsing date:", error);
      return false;
    }
  };

  const getStatusDisplay = (request) => {
    // For grouped requests, check if any slot has passed
    if (request.slots && request.slots.length > 0) {
      const hasPassedSlots = request.slots.some(
        (slot) =>
          isDatePassed(request.date, slot.time) && slot.status === "pending"
      );
      if (hasPassedSlots && request.status === "pending") {
        return "mixed";
      }
    } else {
      // Fallback for single requests
      if (
        isDatePassed(request.date, request.time) &&
        request.status === "pending"
      ) {
        return "missed";
      }
    }
    return request.status;
  };

  const handleRequestClick = async (request) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/invitation-details?invitationId=${request.id}`,
        { withCredentials: true }
      );
      setSelectedRequest(response.data);
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching request details:", error);
    }
  };

  const handleAcceptReject = async (invitationId, action) => {
    try {
      setConfirmationLoading(true);

      // Check if we're dealing with grouped requests (multiple slots)
      const request = requests.find((r) => r.id === invitationId);

      if (request && request.slots && request.slots.length > 1) {
        // Use group response for multiple slots
        const invitationIds = request.slots.map((slot) => slot.id);
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/respond-invitation-group`,
          {
            invitationIds,
            action,
          },
          { withCredentials: true }
        );
      } else {
        // Use single response for single slot
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/respond-invitation`,
          {
            invitationId,
            action,
          },
          { withCredentials: true }
        );
      }

      // Refresh requests
      fetchRequests();
      setShowModal(false);
      setSelectedRequest(null);
      setShowConfirmationModal(false);
      setConfirmationAction(null);

      // Show success toast
      toast.success(`Request ${action} successfully!`);
    } catch (error) {
      console.error(`Error ${action} request:`, error);
      toast.error(`Failed to ${action} request. Please try again.`);
    } finally {
      setConfirmationLoading(false);
    }
  };

  const handleOpenGoogleMaps = (coordinates) => {
    if (coordinates) {
      const [lat, lng] = coordinates.split(",");
      const url = `https://www.google.com/maps?q=${lat},${lng}`;
      window.open(url, "_blank");
    }
  };

  const handleShowConfirmation = (invitationId, action) => {
    setConfirmationAction({ invitationId, action });
    setShowConfirmationModal(true);
  };

  const handleConfirmAction = () => {
    if (confirmationAction) {
      handleAcceptReject(
        confirmationAction.invitationId,
        confirmationAction.action
      );
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmationModal(false);
    setConfirmationAction(null);
  };

  // Pagination
  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(
    indexOfFirstRequest,
    indexOfLastRequest
  );
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="sport-requests-container">
          <div className="loading">Loading sport requests...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header pageType="arena-player-req" />
      <div className="sport-requests-container">
        <div className="sport-requests-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          <h1>Sport Requests</h1>
        </div>

        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              placeholder="Search by player name or arena..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="status-filter">
            <FontAwesomeIcon icon={faFilter} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="missed">Missed</option>
            </select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="results-summary">
          Showing {filteredRequests.length} of {requests.length} requests
        </div>

        {/* Requests List */}
        <div className="requests-list">
          {currentRequests.length === 0 ? (
            <div className="no-requests">
              {requests.length === 0
                ? "You haven't received any sport requests yet."
                : "No requests match your current filters."}
            </div>
          ) : (
            currentRequests.map((request) => (
              <div
                key={request.id}
                className="request-card"
                onClick={() => handleRequestClick(request)}
              >
                <div className="request-content">
                  <div className="request-user">
                    <FontAwesomeIcon icon={faUser} />
                    <span className="player-name">{request.inviter_name}</span>
                  </div>

                  <div className="request-arena">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    <span>{request.arena_name}</span>
                  </div>

                  <div className="request-time">
                    <FontAwesomeIcon icon={faClock} />
                    <span>
                      {formatDate(request.date)}
                      {request.slots && request.slots.length > 1
                        ? ` - ${request.slots.length} slots (${formatTime(
                            request.slots[0].time
                          )} - ${formatTime(
                            request.slots[request.slots.length - 1].time
                          )})`
                        : request.slots && request.slots.length === 1
                        ? ` at ${formatTime(request.slots[0].time)}`
                        : request.time
                        ? ` at ${formatTime(request.time)}`
                        : ""}
                    </span>
                  </div>

                  <div
                    className={`request-status ${getStatusDisplay(request)}`}
                  >
                    {getStatusDisplay(request)}
                  </div>
                </div>

                <div className="request-actions">
                  <span className="view-details">View Details →</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`pagination-btn ${
                  currentPage === page ? "active" : ""
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        )}

        {/* Request Details Modal */}
        {showModal && selectedRequest && (
          <div className="request-modal-overlay">
            <div className="request-modal">
              <div className="modal-header">
                <h3>Sport Request Details</h3>
                <button
                  className="close-modal"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedRequest(null);
                  }}
                >
                  ×
                </button>
              </div>

              <div className="modal-content">
                {/* Inviter Details */}
                <div className="section">
                  <h4>
                    <FontAwesomeIcon icon={faUser} /> Player Details
                  </h4>
                  <div className="details-grid">
                    <div className="detail-item">
                      <strong>Name:</strong> {selectedRequest.inviter_name}
                    </div>
                    <div className="detail-item">
                      <strong>Email:</strong> {selectedRequest.inviter_email}
                    </div>
                    <div className="detail-item">
                      <strong>Contact:</strong>{" "}
                      {selectedRequest.inviter_contact || "N/A"}
                    </div>
                  </div>
                </div>

                {/* Arena Details */}
                <div className="section">
                  <h4>
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> Arena Details
                  </h4>
                  <div className="details-grid">
                    <div className="detail-item">
                      <strong>Arena:</strong> {selectedRequest.arena_name}
                    </div>
                    <div className="detail-item">
                      <strong>Phone:</strong> {selectedRequest.arena_telephone}
                    </div>
                    <div className="detail-item">
                      <strong>Facility:</strong>{" "}
                      {selectedRequest.about_facility}
                    </div>
                    <div className="detail-item">
                      <strong>Date:</strong> {formatDate(selectedRequest.date)}
                    </div>
                    {selectedRequest.slots &&
                    selectedRequest.slots.length > 1 ? (
                      <div className="detail-item">
                        <strong>Time Slots:</strong>
                        <div className="time-slots-list">
                          {selectedRequest.slots.map((slot, index) => (
                            <span key={slot.id} className="time-slot">
                              {formatTime(slot.time)}
                              {index < selectedRequest.slots.length - 1 && ", "}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : selectedRequest.slots &&
                      selectedRequest.slots.length === 1 ? (
                      <div className="detail-item">
                        <strong>Time:</strong>{" "}
                        {formatTime(selectedRequest.slots[0].time)}
                      </div>
                    ) : selectedRequest.time ? (
                      <div className="detail-item">
                        <strong>Time:</strong>{" "}
                        {formatTime(selectedRequest.time)}
                      </div>
                    ) : null}
                    <div className="detail-item">
                      <strong>Address:</strong> {selectedRequest.arena_address}
                    </div>
                    {selectedRequest.coordinates && (
                      <div className="detail-item">
                        <strong>Location:</strong>
                        <button
                          className="map-button"
                          onClick={() =>
                            handleOpenGoogleMaps(selectedRequest.coordinates)
                          }
                        >
                          View on Google Maps
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Message */}
                {selectedRequest.message && (
                  <div className="section">
                    <h4>Message</h4>
                    <p className="message">{selectedRequest.message}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="modal-actions">
                {(() => {
                  // Check if all slots have passed for grouped requests
                  const allSlotsPassed =
                    selectedRequest.slots && selectedRequest.slots.length > 0
                      ? selectedRequest.slots.every((slot) =>
                          isDatePassed(selectedRequest.date, slot.time)
                        )
                      : isDatePassed(
                          selectedRequest.date,
                          selectedRequest.time
                        );

                  if (allSlotsPassed && selectedRequest.status === "pending") {
                    return (
                      <div className="missed-status">
                        This request has expired
                      </div>
                    );
                  } else if (selectedRequest.status === "pending") {
                    return (
                      <>
                        <button
                          className="accept-btn"
                          onClick={() =>
                            handleShowConfirmation(
                              selectedRequest.invitation_id ||
                                selectedRequest.id,
                              "accepted"
                            )
                          }
                        >
                          Accept{" "}
                          {selectedRequest.slots &&
                          selectedRequest.slots.length > 1
                            ? `All ${selectedRequest.slots.length} Slots`
                            : ""}
                        </button>
                        <button
                          className="reject-btn"
                          onClick={() =>
                            handleShowConfirmation(
                              selectedRequest.invitation_id ||
                                selectedRequest.id,
                              "rejected"
                            )
                          }
                        >
                          Reject{" "}
                          {selectedRequest.slots &&
                          selectedRequest.slots.length > 1
                            ? `All ${selectedRequest.slots.length} Slots`
                            : ""}
                        </button>
                      </>
                    );
                  } else {
                    return (
                      <div
                        className={`status-display ${selectedRequest.status}`}
                      >
                        {selectedRequest.status.charAt(0).toUpperCase() +
                          selectedRequest.status.slice(1)}
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal for Accept/Reject Actions */}
        <ConfirmationModal
          isOpen={showConfirmationModal}
          title={`Confirm ${
            confirmationAction?.action === "accepted" ? "Accept" : "Reject"
          } Request`}
          message={`Are you sure you want to ${
            confirmationAction?.action === "accepted" ? "accept" : "reject"
          } this sport request?`}
          confirmText={`${
            confirmationAction?.action === "accepted" ? "Accept" : "Reject"
          } Request`}
          cancelText="Cancel"
          onConfirm={handleConfirmAction}
          onCancel={handleCancelConfirmation}
          isLoading={confirmationLoading}
          confirmButtonClass={
            confirmationAction?.action === "accepted"
              ? "bg-green-600 text-white hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
              : "bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
          }
        />
      </div>
    </div>
  );
};

export default SportRequests;
